import React from "react";

import {
    BlockModel,
    ElementTypes,
    EmbedBlockModel,
    LinkBlockModel,
    ReferenceBlockModel,
    RichtextBlockModel,
    YoutubeBlockModel,
} from "@courses-models/course";

import { Store } from "@apps/viewer/src/ts/store";
import { ContentModel, Engine, IframeModel, IframeTargets } from "@courses-pm2/api";
import { urls } from "@courses-pm2/api/src/ts/config";

/**
 * Helper that'll render the given block model into a React element,
 * automatically fetching the parts of the specific block component based on
 * the type of model.
 */
export async function renderBlock({ model, store }: {
    model: BlockModel;
    store: Store;
}): Promise<React.JSX.Element> {
    //
    // HANDLE EMBED BLOCKS
    //
    if (model instanceof EmbedBlockModel) {
        const { EmbedBlock } = await import("@apps/viewer/src/ts/components/block/EmbedBlock");

        return <EmbedBlock model={model} />;
    }

    //
    // HANDLE LINK BLOCKS
    //
    if (model instanceof LinkBlockModel) {
        const { LinkBlock } = await import("@apps/viewer/src/ts/components/block/LinkBlock");

        return <LinkBlock model={model} />;
    }

    //
    // HANDLE REFERENCE BLOCKS
    //
    if (model instanceof ReferenceBlockModel) {
        const { ReferenceBlock } = await import("@apps/viewer/src/ts/components/block/ReferenceBlock");

        // Fetch the content that's being referenced
        let content: ContentModel;

        switch (model.element.type) {
            case ElementTypes.PAGE:
                content = await store.engine.getPage({
                    pageId: model.element.id,
                    portalId: model.product.id,
                });
                break;

            case ElementTypes.IBOOK:
                store.engine.announce(IframeModel);
                await store.engine.waitForPromises();

                content = new ContentModel({
                    engine: store.engine,
                    isAuthorized: true,
                    isScrambled: false,
                    elements: [
                        new IframeModel({
                            isScrambled: false,

                            engine: store.engine,
                            portalId: model.product.id,

                            aspectRatio: {
                                width: 16,
                                height: 9,
                            },
                            minSize: {
                                width: 0,
                                height: 0,
                            },
                            maxSize: undefined,

                            previewImage: undefined,
                            needsAutoGeneratedPreviewImage: true,

                            autoActivate: true,
                            inPageEmbed: true,
                            target: IframeTargets.WINDOW,
                            src: `${urls.ibook4}/${model.element.id}/`,
                        }),
                    ],
                });
                break;

            default:
                content = await store.engine.getElement({
                    elementId: model.element.id,
                    portalId: model.product.id,
                });
        }

        return <ReferenceBlock model={model} content={content} />;
    }

    //
    // HANDLE RICHTEXT BLOCKS
    //
    if (model instanceof RichtextBlockModel) {
        const { RichtextBlock } = await import("@apps/viewer/src/ts/components/block/RichtextBlock");

        return <RichtextBlock model={model} content={await model.getContent()} />;
    }

    //
    // HANDLE YOUTUBE BLOCKS
    //
    if (model instanceof YoutubeBlockModel) {
        const { YoutubeBlock } = await import("@apps/viewer/src/ts/components/block/YoutubeBlock");

        return <YoutubeBlock model={model} />;
    }

    // Throw an error, if we were unable to determine kind of component needed
    // to render the component
    throw new Error("renderBlock(): Unable to determine block type.");
}
